/**
 * The "Manage settings" dialog's logic (routes/platform/plant-agent-settings.tsx; agent 0.3.0, migration 0218; the
 * route is PUT /v1/ingest/agents/{agent_id}/settings, backend manage.go): the form, its PUT body, the checks the API
 * makes, and its refusals in plain words. Pure.
 *
 * Each setting has a "Manage from Sahas Forge" box: a ticked one goes in the body and is managed (the agent locks it on
 * the PC, or applies it once with control "local"); an unticked one is left out and stays the PC's own. Bandwidth and
 * Updates are each one setting of the dialog: managed, Bandwidth sends both shares, and Updates sends the policy, the
 * pin when one is typed, and urgent only when it is ticked. Control is sent only as "local": "remote" is the default.
 *
 * The PUT replaces every managed setting, and some are not this dialog's (folders, patterns and remote browse, set with
 * divineiotctl, and any key a newer agent adds): a save carries each of those through as it stands (keptSettings), so
 * saving the dialog never drops them. "Stop managing" sends {} and stops managing them as well.
 *
 * Agent 0.4.0 (migration 0231) adds two settings with no box of their own, and the dialog owns both: keptSettings does
 * not carry them through. "Allow saving to a pendrive" has three states: not managed, on, off. "Explore consent" is what
 * the plant agreed an explore request may do: it is sent when anything is agreed, and left out when nothing is, which
 * is how consent is taken back.
 */
import type {
  IngestAgent,
  IngestExploreConsent,
  IngestManagedSettings,
  IngestPath,
  IngestSendMode,
  IngestUpdatePolicy,
} from "@/lib/api/ingest-agents";
import { errorCodeOf, errorDetailOf, isNetworkError, statusOf } from "@/lib/api/error";
import { CONSENT_FLAGS, type ConsentFlag } from "@/lib/plant-agents";

/** The dialog's settings that have a "Manage from Sahas Forge" box. */
export type ManagedKey =
  | "mode"
  | "samples_per_kind"
  | "drop"
  | "bandwidth"
  | "updates"
  | "path"
  | "interval_minutes"
  | "max_per_pass";

export const MANAGED_KEYS: readonly ManagedKey[] = [
  "mode",
  "samples_per_kind",
  "drop",
  "bandwidth",
  "updates",
  "path",
  "interval_minutes",
  "max_per_pass",
];

/** A setting a problem is shown against: one with a box, or the explore consent. */
export type SettingKey = ManagedKey | "explore_consent";

/** Saving to a pendrive: `default` is not managed (the PC's mode decides: on in probe mode, off in operational mode). */
export type PendriveChoice = "default" | "on" | "off";

/** The explore consent as the dialog holds it: a box for each opt-in, and the folders a deep scan may cover, one a row. */
export type ConsentForm = Record<ConsentFlag, boolean> & { roots: string[] };

/** What the dialog holds: which settings are managed, and each value as chosen or typed. */
export interface SettingsForm {
  control: "remote" | "local";
  manage: Record<ManagedKey, boolean>;
  mode: IngestSendMode;
  samples: string;
  drop: boolean;
  catchUpPct: number;
  afterPct: number;
  policy: IngestUpdatePolicy;
  pin: string;
  urgent: boolean;
  path: IngestPath;
  interval: string;
  perPass: string;
  pendrive: PendriveChoice;
  consent: ConsentForm;
}

/** The limits the API checks (manage.go validate, explore.go). */
export const LIMITS = {
  samples: [1, 20],
  bandwidth: [5, 100],
  interval: [1, 1440],
  perPass: [1, 1000],
  /** Explore consent: the most folders, and the most characters in one. */
  roots: 8,
  rootChars: 260,
} as const;

/** What an unmanaged setting shows: the agent's own defaults (divineiotctl agents settings). */
export const DEFAULTS = {
  mode: "probe" as IngestSendMode,
  samples: 3,
  drop: true,
  catchUpPct: 50,
  afterPct: 100,
  policy: "auto" as IngestUpdatePolicy,
  path: "auto" as IngestPath,
  interval: 15,
  perPass: 50,
  pendrive: "default" as PendriveChoice,
};

const PIN = /^[0-9]{1,4}\.[0-9]{1,4}\.[0-9]{1,6}$/;

/** The consent form for what the PC holds now: a ticked box for each opt-in agreed, and its folders. */
function consentFormOf(c: IngestExploreConsent | undefined): ConsentForm {
  return {
    processes: c?.processes ?? false,
    arp: c?.arp ?? false,
    user: c?.user ?? false,
    whole_drives: c?.whole_drives ?? false,
    opcua_test: c?.opcua_test ?? false,
    sql_test: c?.sql_test ?? false,
    net_test: c?.net_test ?? false,
    roots: [...(c?.deep_roots ?? [])],
  };
}

/** The form for what is managed now: a set key is ticked with its value; the rest show the defaults. */
export function formOfSettings(s: IngestManagedSettings | null | undefined): SettingsForm {
  const m = s ?? {};
  return {
    control: m.control === "local" ? "local" : "remote",
    manage: {
      mode: m.mode !== undefined,
      samples_per_kind: m.samples_per_kind !== undefined,
      drop: m.drop !== undefined,
      bandwidth: m.bandwidth !== undefined,
      updates: m.updates !== undefined,
      path: m.path !== undefined,
      interval_minutes: m.interval_minutes !== undefined,
      max_per_pass: m.max_per_pass !== undefined,
    },
    mode: m.mode ?? DEFAULTS.mode,
    samples: String(m.samples_per_kind ?? DEFAULTS.samples),
    drop: m.drop ?? DEFAULTS.drop,
    catchUpPct: m.bandwidth?.catch_up_pct ?? DEFAULTS.catchUpPct,
    afterPct: m.bandwidth?.after_pct ?? DEFAULTS.afterPct,
    policy: m.updates?.policy ?? DEFAULTS.policy,
    pin: m.updates?.pin ?? "",
    urgent: m.updates?.urgent ?? false,
    path: m.path ?? DEFAULTS.path,
    interval: String(m.interval_minutes ?? DEFAULTS.interval),
    perPass: String(m.max_per_pass ?? DEFAULTS.perPass),
    pendrive: m.pendrive_save === undefined ? DEFAULTS.pendrive : m.pendrive_save ? "on" : "off",
    consent: consentFormOf(m.explore_consent),
  };
}

/** A whole number typed in a field, within [lo, hi], or null. */
function wholeIn(s: string, lo: number, hi: number): number | null {
  const t = s.trim();
  if (!/^\d{1,6}$/.test(t)) return null;
  const n = Number(t);
  return n >= lo && n <= hi ? n : null;
}

const pctOk = (n: number) => Number.isInteger(n) && n >= LIMITS.bandwidth[0] && n <= LIMITS.bandwidth[1];

/**
 * The managed keys this dialog edits; every other managed key is kept through a save (keptSettings). The pendrive and
 * the explore consent (agent 0.4.0) are the dialog's too: the form decides both, so a save never carries the PC's own
 * value of either through.
 */
const DIALOG_KEYS: ReadonlySet<string> = new Set<string>(["control", ...MANAGED_KEYS, "pendrive_save", "explore_consent"]);

/**
 * The managed settings this dialog does not edit, as they stand: folders, patterns and remote browse, and any key a
 * newer agent's settings carry. The PUT replaces them all, so a save that left them out would stop managing them.
 */
export function keptSettings(current: IngestManagedSettings | null | undefined): IngestManagedSettings {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(current ?? {})) {
    if (!DIALOG_KEYS.has(k) && v !== undefined) out[k] = v;
  }
  return out as IngestManagedSettings;
}

/** The kept settings in words, in a fixed order: what the dialog's control choice also covers. */
const KEPT_WORDS: Record<string, string> = {
  folders: "watched folders",
  patterns: "file patterns",
  remote_browse: "remote browse",
};

/**
 * One folder of a deep scan, in the API's spelling (the drive letter in capitals, single backslashes, none at the end),
 * or what is wrong with it. The same rules as the API's cleanRoot (platform-core ingestagent/explore.go): an absolute
 * drive path only; no network or device path, no `.` or `..` step, nothing Windows reads as a stream or a wildcard.
 */
export function cleanRoot(input: string): { root: string } | { why: string } {
  const s = input.trim().replaceAll("/", "\\");
  if (s === "") return { why: "is empty" };
  if (s.length > LIMITS.rootChars) return { why: `is over ${LIMITS.rootChars} characters` };
  if (/\p{Cc}/u.test(s)) return { why: "holds a control character" };
  if (s.startsWith("\\\\")) return { why: "is a network or device path: write a drive path like D:\\X\\Y" };
  if (!/^[A-Za-z]:\\/.test(s)) return { why: "is not a full drive path like D:\\X\\Y" };
  const head = `${s[0].toUpperCase()}:\\`;
  const rest = s.slice(3).replace(/\\$/, "");
  if (rest === "") return { root: head };
  const names = rest.split("\\");
  for (const name of names) {
    if (name === "") return { why: "has an empty folder name (two backslashes in a row)" };
    if (/^\.+$/.test(name)) return { why: 'has a "." or ".." step: write the full path' };
    if (/[<>:"|?*]/.test(name)) return { why: 'has a character Windows does not allow in a name (< > : " | ? *)' };
    if (/[. ]$/.test(name)) return { why: "has a folder name that ends in a dot or a space" };
  }
  return { root: head + names.join("\\") };
}

/** Whether the plant agreed to anything: a box ticked, or a folder written. */
export function consentAgreed(c: ConsentForm): boolean {
  return CONSENT_FLAGS.some((k) => c[k]) || c.roots.some((r) => r.trim() !== "");
}

/**
 * The explore consent for the PUT body, with its folders in the API's spelling (a row left empty is ignored, and a
 * folder written twice counts once), or why it can't be sent. `given_by` and `given_at` are the cloud's: never sent.
 */
function consentOfForm(
  c: ConsentForm,
  held: readonly string[],
): { consent: IngestExploreConsent } | { why: string; row?: number } {
  // The rows with something in them, each with its place in the list, so a problem can name the row it is in.
  const typed = c.roots.map((r, row) => ({ raw: r.trim(), row })).filter((r) => r.raw !== "");
  if (typed.length > LIMITS.roots) {
    return { why: `Explore consent: at most ${LIMITS.roots} folders.`, row: typed[LIMITS.roots].row };
  }
  // A folder the PC holds already is not checked again: the API took it, and a rule of ours must never stop a save.
  const known = new Map(held.map((r) => [r.toLowerCase(), r]));
  const seen = new Set<string>();
  const roots: string[] = [];
  for (const { raw, row } of typed) {
    let root = known.get(raw.toLowerCase());
    if (root === undefined) {
      const made = cleanRoot(raw);
      if ("why" in made) return { why: `Explore consent: the folder ${raw} ${made.why}.`, row };
      root = made.root;
    }
    if (root.length === 3 && !c.whole_drives) {
      return { why: `Explore consent: ${root} is a whole drive. Tick Whole drives as well, or write a folder on it.`, row };
    }
    if (!seen.has(root.toLowerCase())) {
      seen.add(root.toLowerCase());
      roots.push(root);
    }
  }
  return {
    consent: {
      processes: c.processes,
      arp: c.arp,
      user: c.user,
      whole_drives: c.whole_drives,
      opcua_test: c.opcua_test,
      sql_test: c.sql_test,
      net_test: c.net_test,
      deep_roots: roots,
    },
  };
}

/** A problem found before sending. */
export interface SettingsInvalid {
  /** The API's code for it. */
  error: string;
  /** The setting it is in. */
  key: SettingKey;
  /** Words for this exact problem, when the code's are too general. */
  detail?: string;
  /** The folder it is in, as a row of the consent's list (from 0), when it is in one. */
  row?: number;
}

/**
 * The managed settings the dialog does not edit (keptSettings), in plain words for the control choice's note: the
 * known ones by name, any other (a newer agent's key) as "other settings", once. [] when there are none.
 */
export function keptWords(current: IngestManagedSettings | null | undefined): string[] {
  const keys = Object.keys(keptSettings(current));
  const words = Object.keys(KEPT_WORDS).filter((k) => keys.includes(k)).map((k) => KEPT_WORDS[k]);
  if (keys.some((k) => !(k in KEPT_WORDS))) words.push("other settings");
  return words;
}

/** Words joined as a sentence lists them: "a", "a and b", "a, b and c". */
export function listInWords(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * The PUT body's settings: the ones ticked in the dialog, and those it does not edit kept as they stand
 * (keptSettings(current)). Or the first problem, as the API's code would name it, and the setting it is in.
 */
export function settingsOfForm(
  f: SettingsForm,
  current?: IngestManagedSettings | null,
): { settings: IngestManagedSettings } | SettingsInvalid {
  const out: IngestManagedSettings = { ...keptSettings(current) };
  if (f.manage.mode) out.mode = f.mode;
  if (f.manage.samples_per_kind) {
    const n = wholeIn(f.samples, ...LIMITS.samples);
    if (n === null) return { error: "invalid_samples_per_kind", key: "samples_per_kind" };
    out.samples_per_kind = n;
  }
  if (f.manage.drop) out.drop = f.drop;
  if (f.manage.bandwidth) {
    if (!pctOk(f.catchUpPct) || !pctOk(f.afterPct)) return { error: "invalid_bandwidth", key: "bandwidth" };
    out.bandwidth = { catch_up_pct: f.catchUpPct, after_pct: f.afterPct };
  }
  if (f.manage.updates) {
    const pin = f.pin.trim();
    if (pin && !PIN.test(pin)) return { error: "invalid_update_pin", key: "updates" };
    out.updates = { policy: f.policy, ...(pin ? { pin } : {}), ...(f.urgent ? { urgent: true } : {}) };
  }
  if (f.manage.path) out.path = f.path;
  if (f.manage.interval_minutes) {
    const n = wholeIn(f.interval, ...LIMITS.interval);
    if (n === null) return { error: "invalid_interval_minutes", key: "interval_minutes" };
    out.interval_minutes = n;
  }
  if (f.manage.max_per_pass) {
    const n = wholeIn(f.perPass, ...LIMITS.perPass);
    if (n === null) return { error: "invalid_max_per_pass", key: "max_per_pass" };
    out.max_per_pass = n;
  }
  if (f.pendrive !== "default") out.pendrive_save = f.pendrive === "on";
  if (consentAgreed(f.consent)) {
    const made = consentOfForm(f.consent, current?.explore_consent?.deep_roots ?? []);
    if ("why" in made) {
      return {
        error: "invalid_explore_consent",
        key: "explore_consent",
        detail: made.why,
        ...(made.row !== undefined ? { row: made.row } : {}),
      };
    }
    out.explore_consent = made.consent;
  }
  if (Object.keys(out).length > 0 && f.control === "local") return { settings: { control: "local", ...out } };
  return { settings: out };
}

/** Whether saving the form changes anything worth sending: something managed now, or something managed before. */
export function canSave(f: SettingsForm, agent: Pick<IngestAgent, "managed_settings">): boolean {
  return (
    MANAGED_KEYS.some((k) => f.manage[k]) ||
    f.pendrive !== "default" ||
    consentAgreed(f.consent) ||
    Object.keys(agent.managed_settings ?? {}).length > 0
  );
}

/** The API's 400 codes for settings (and the same checks made here first), in words. */
export const SETTINGS_CODE_WORDS: Record<string, string> = {
  invalid_control: "Choose whether the settings are locked on the PC or the PC can change them.",
  invalid_mode: "Choose Probe or Operational.",
  invalid_samples_per_kind: `Samples per kind: a whole number from ${LIMITS.samples[0]} to ${LIMITS.samples[1]}.`,
  invalid_path: "Choose a route: automatic, direct only, or through a gateway only.",
  invalid_interval_minutes: `Interval: a whole number of minutes from ${LIMITS.interval[0]} to ${LIMITS.interval[1]}.`,
  invalid_max_per_pass: `Files per pass: a whole number from ${LIMITS.perPass[0]} to ${LIMITS.perPass[1]}.`,
  invalid_bandwidth: `Bandwidth: from ${LIMITS.bandwidth[0]}% to ${LIMITS.bandwidth[1]}%.`,
  invalid_update_policy: "Choose an update policy: automatic, notify only, or off.",
  invalid_update_pin: "Pin to version: a version like 0.3.1, or leave it empty.",
};

/** A failed save in words: the API's code when it names one, else why it could not be sent. */
export function settingsErrorWords(err: unknown): string {
  const status = statusOf(err);
  const code = errorCodeOf(err);
  if (code && SETTINGS_CODE_WORDS[code]) return SETTINGS_CODE_WORDS[code];
  if (code === "identity_revoked" || status === 409) return "This identity is revoked: its settings can't be managed.";
  if (code === "agent_not_found" || status === 404) return "This plant PC is no longer in the org.";
  if (status === 401) return "Your session has ended: sign in again.";
  if (status === 403) return "Only an org admin can manage a plant PC's settings.";
  if (status === 400) return errorDetailOf(err) ?? `The server did not take these settings (${code ?? "invalid"}).`;
  if (isNetworkError(err)) return "The platform could not be reached. Check the connection and try again.";
  return "Couldn't save the settings. Try again in a moment.";
}
